import { createContext, useContext, useState, useCallback, type ReactNode } from 'react'

interface ChatBotContextValue {
  isOpen: boolean
  setIsOpen: (open: boolean) => void
  pendingMessage: string | null
  openWithMessage: (message: string) => void
  consumePendingMessage: () => string | null
}

const ChatBotContext = createContext<ChatBotContextValue | null>(null)

export function ChatBotProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false)
  const [pendingMessage, setPendingMessage] = useState<string | null>(null)

  const openWithMessage = useCallback((message: string) => {
    setPendingMessage(message)
    setIsOpen(true)
  }, [])

  const consumePendingMessage = useCallback(() => {
    const msg = pendingMessage
    setPendingMessage(null)
    return msg
  }, [pendingMessage])

  return (
    <ChatBotContext.Provider value={{ isOpen, setIsOpen, pendingMessage, openWithMessage, consumePendingMessage }}>
      {children}
    </ChatBotContext.Provider>
  )
}

export function useChatBot() {
  const context = useContext(ChatBotContext)
  if (!context) {
    throw new Error('useChatBot must be used within a ChatBotProvider')
  }
  return context
}
